import type { PlanOperation } from '../../../types/plan';
import type { PlanningActionType } from '../../../types/planManagement';

export type ShadeStructureAction = Extract<
  PlanningActionType,
  'outbuilding' | 'solid-fence' | 'lamp-post'
>;

interface ShadeStructurePreset {
  action: ShadeStructureAction;
  label: string;
  hint: string;
  kind: 'infrastructure' | 'device';
  subtype: string;
  name: string;
  singleCell: boolean;
  parameters: Record<string, unknown>;
}

export const SHADE_STRUCTURE_PRESETS: Record<ShadeStructureAction, ShadeStructurePreset> = {
  outbuilding: {
    action: 'outbuilding',
    label: 'Хоз. постройка',
    hint: 'Высота 3.5 м (около 11.5 ft)',
    kind: 'infrastructure',
    subtype: 'outbuilding',
    name: 'Хозяйственная постройка',
    singleCell: false,
    parameters: {
      casts_shade: true,
      // Regular barn ridge height. Small sheds are ~2.5 m; pick that per-object if needed.
      shade_height_m: 3.5,
      shade_opacity: 1,
    },
  },
  'solid-fence': {
    action: 'solid-fence',
    label: 'Сплошной забор',
    hint: 'Высота 2 м',
    kind: 'infrastructure',
    subtype: 'solid_fence',
    name: 'Сплошной забор',
    singleCell: false,
    parameters: {
      casts_shade: true,
      shade_height_m: 2,
      shade_opacity: 1,
    },
  },
  'lamp-post': {
    action: 'lamp-post',
    label: 'Фонарь',
    hint: 'Высота 2 м (около 7 ft), одна клетка',
    kind: 'device',
    subtype: 'lamp_post',
    name: 'Отдельно стоящий фонарь',
    singleCell: true,
    parameters: {
      casts_shade: true,
      shade_height_m: 2,
      shade_width_m: 0.15,
      shade_opacity: 1,
    },
  },
};

export const SHADE_STRUCTURE_ACTIONS = Object.keys(
  SHADE_STRUCTURE_PRESETS,
) as ShadeStructureAction[];

export const isShadeStructureAction = (
  action: PlanningActionType,
): action is ShadeStructureAction => action in SHADE_STRUCTURE_PRESETS;

export const buildShadeStructureOperation = (
  action: ShadeStructureAction,
  cells: Array<{ row: number; col: number }>,
): PlanOperation => {
  const preset = SHADE_STRUCTURE_PRESETS[action];
  return {
    op: 'createStructuralObject',
    cells,
    kind: preset.kind,
    subtype: preset.subtype,
    name: preset.name,
    parameters: { ...preset.parameters },
  };
};
